import React, { useMemo } from "react";
import { styled, SxProps, Stack, Checkbox, Typography, Button, Chip, Link, Tooltip } from "@mui/material";

import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import SecurityIcon from "@mui/icons-material/Security";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import SyncIcon from "@mui/icons-material/Sync";
import { DbOrderBasketItemInfos } from "../../../../Core/Api/Types/db-order-basket-item-infos";
import OrderProductIcon from "../../../Misc/OrderProductIcon/order-product-icon";
import { GetOrderProductReadableType, GetTitleRouterPath, UserHasAcl } from "../../../../Utils/helpers";
import { BasketItemStatus } from "../../../../Core/Api/Types/basket-item-status";
import {
  CopyToAllBasketItems,
  IsBasketItemMissingAsset,
} from "../../../../Utils/Helpers/IntelliTranskode/intellitranskode-helpers";
import { Link as RouterLink } from "react-router";
import { ObjectLayer } from "../../../../Core/Api/Types/object-layer";
import { routePaths } from "../../../../Router/router";
import {
  DuplicateItem,
  GetBasket,
  GetBasketBundleProfiles,
  GetBasketProfiles,
  RemoveItem,
  UpdateItemOnBasket,
} from "../../../../Redux/Reducers/redux-slice-basket";
import { useAppDispatch, useAppSelector } from "../../../../Redux/redux-store";
import { Box } from "@mui/system";
import { useSelector } from "react-redux";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import GlobalConst from "../../../../Core/Utils/Constants/globals";
import { OrderProductType } from "../../../../Core/Api/Types/order-product-type";
import { TitleShouldBeWatermarked } from "../../../../Core/Utils/asset-object-utils";
import { ObjectType } from "../../../../Core/Api/Types/object-type";
import { useTranslation } from "react-i18next";

export type ConfigOrderContentsRowProps = {
  sx?: SxProps;
  checked?: boolean;
  id: number;
  title: string;
  item: DbOrderBasketItemInfos;
  onChange: (id: number) => void;
  onApplyToAll: (srcItem: DbOrderBasketItemInfos, copiedItems: DbOrderBasketItemInfos[]) => void;
};

const ConfigOrderContentsRow: React.FC<ConfigOrderContentsRowProps> = (props) => {
  const { t } = useTranslation();
  const { checked = false, item, onChange, onApplyToAll, id } = props;
  const fieldId = React.useId();
  const dispatch = useAppDispatch();
  const basket = useSelector(GetBasket);
  const profiles = useAppSelector(GetBasketProfiles);
  const bundleProfiles = useAppSelector(GetBasketBundleProfiles);

  const handleChange = React.useCallback(() => {
    if (item.status === BasketItemStatus.Loading) return;

    onChange(id);
  }, [onChange, id, item.status]);

  const onRemove = (itemId: number) => {
    dispatch(RemoveItem(itemId));
  };

  const applyConfigToAll = (itemId: number) => {
    // Call this method to deep copy properties and returns modified items
    let updatedItems = CopyToAllBasketItems(basket.items!, itemId, true);

    if (!item.selectedContent)
      // Will be treated on basket.tsx with dedicated right page panel
      for (let i = 0; i < updatedItems.length; ++i) dispatch(UpdateItemOnBasket(updatedItems[i]));

    // Set Options will be done on basket.tsx
    onApplyToAll && onApplyToAll(basket.items!.find((x) => x.id === itemId)!, updatedItems);
  };

  const duplicateConfig = (itemId: number) => {
    dispatch(DuplicateItem(itemId));
  };

  const missingAssets = useMemo(() => {
    return IsBasketItemMissingAsset(item, profiles, bundleProfiles);
  }, [item, profiles, bundleProfiles]);

  const shouldShowWatermarkWarning: boolean = useMemo(() => {
    if (!item.titleInfos) return false;
    let should = TitleShouldBeWatermarked(item.titleInfos);

    return should && item.selectedProduct !== null;
  }, [item.id, item.selectedProduct?.watermarkSecurity]);

  const isWartermarkable: boolean | null = useMemo(
    () =>
      item.selectedProduct &&
      item.selectedProduct.type &&
      ![
        OrderProductType.TranscodeComponentsLegacy,
        OrderProductType.TranscodeDownscaleAsMezz,
        OrderProductType.ManualTasks,
      ].includes(item.selectedProduct.type) &&
      (item.objectLayer === ObjectLayer.Title ||
        (item.objectLayer === ObjectLayer.Asset && item.assetInfos?.type === ObjectType.Video)),
    [item.objectLayer, item.assetInfos, item.selectedProduct?.type]
  );

  return (
    <label id={fieldId}>
      <Row direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} checked={checked}>
        <Stack direction="row" alignItems="center" flex={0.6} spacing={0.45}>
          <Checkbox checked={checked} onChange={handleChange} id={fieldId} />
          <Box fontWeight={500} color={checked ? "primary.text" : "white"}>
            <Link
              underline="hover"
              to={`${item.objectLayer === ObjectLayer.Title ? GetTitleRouterPath() : routePaths.asset.path}/${
                item.objectId
              }`}
              component={RouterLink}
              color="text.primary"
              sx={{ fontWeight: 500 }}
              target="_blank"
            >
              {item.assetInfos?.name}
              {item.episodeNb ? " - EPISODE " + item.episodeNb : ""}
              {item.selectedContent ? " - " + item.selectedContent.name : ""}
              {item.assetInfos?.layer !== ObjectLayer.Clip && item.selectedProduct?.settings?.tgop?.timecodeTrim
                ? " - CLIP " + item.selectedProduct?.settings?.tgop?.timecodeTrim.name
                : ""}
              {item.assetInfos?.layer !== ObjectLayer.Clip && item.selectedProduct?.settings?.timecodeTrim
                ? " - CLIP " + item.selectedProduct?.settings?.timecodeTrim.name
                : ""}
              {item.assetInfos?.layer !== ObjectLayer.Clip &&
              item.selectedProduct?.settings?.cpls?.selectedValue?.timecodeTrim
                ? " - CLIP " + item.selectedProduct?.settings?.cpls?.selectedValue?.timecodeTrim.name
                : ""}
            </Link>

            <Stack
              color="text.tier"
              mt={0.5}
              display={item.durationInMinutes || item.itemReferenceId || item.contractExternalDealId ? "block" : "none"}
              direction="row"
              alignItems="left"
            >
              {item.durationInMinutes !== null && item.durationInMinutes > 0 && (
                <Typography display="inline" variant="body2" mr={1}>
                  {item.durationInMinutes} min
                </Typography>
              )}
              {item.contractExternalDealId && (
                <Typography display="inline" variant="body2" mr={1}>
                  {t("common:contractWithId", { id: item.contractExternalDealId })}
                </Typography>
              )}
              {item.itemReferenceId && (
                <Typography display="inline" variant="body2" color="warning.main">
                  {t("card:body.underModification")}
                </Typography>
              )}
            </Stack>
          </Box>
        </Stack>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flex={0.35} spacing={1.5}>
          {item.status === BasketItemStatus.Loading ? (
            <Typography color="primary.text" display="inline-flex" alignItems="center">
              <SyncIcon sx={{ mr: 0.5 }} /> {GlobalConst.LOADING_TEXT}
            </Typography>
          ) : (
            <>
              {item.selectedProduct ? (
                <>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    {item?.selectedProduct && (
                      <Typography
                        display="inline-flex"
                        alignItems="center"
                        color={
                          checked
                            ? "primary.text"
                            : item.status === BasketItemStatus.OptionsDone
                            ? "success.main"
                            : "warning.main"
                        }
                      >
                        <OrderProductIcon type={item.selectedProduct?.type!} sx={{ marginRight: 1 }} />
                        {GetOrderProductReadableType(item.selectedProduct?.type!)}
                      </Typography>
                    )}
                    {item.selectedProduct?.watermarkSecurity &&
                      isWartermarkable &&
                      item.selectedProduct?.type !== OrderProductType.FaspexSend && (
                        <Typography
                          display="inline-flex"
                          alignItems="center"
                          color={checked ? "primary.text" : 1 === 1 ? "success.main" : "warning.main"}
                        >
                          <SecurityIcon sx={{ marginRight: 1 }} /> {t("common:watermark")}
                        </Typography>
                      )}
                    {item.assetInfos?.type === ObjectType.Video &&
                      shouldShowWatermarkWarning === true &&
                      (!item.selectedProduct?.watermarkSecurity ||
                        item.selectedProduct?.type === OrderProductType.FaspexSend) &&
                      isWartermarkable === true && (
                        <Typography display="inline-flex" alignItems="center" color={"warning.main"}>
                          <SecurityIcon sx={{ marginRight: 1 }} />{" "}
                          {t("card:configOrderLayerContentsRow.body.watermarkableWarning")}
                        </Typography>
                      )}
                    {missingAssets.missing && (
                      <Tooltip title={missingAssets.fields.map((f) => f.message).join(", ")}>
                        <Chip
                          variant="outlined"
                          label={t("chip:label.missingAssetsLowerCase")}
                          color="warning"
                          size="small"
                          icon={<BuildCircleOutlinedIcon />}
                        />
                      </Tooltip>
                    )}
                  </Stack>
                  {item.totalPrice && UserHasAcl(AclTypes.aclOrderPriceRuleMatchEnable) && (
                    <Typography color={checked ? "primary.text" : "text.secondary"}>
                      {item.totalPrice?.price} {item.totalPrice?.currency}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography
                  color={checked ? "primary.text" : "text.secondary"}
                  display="inline-flex"
                  alignItems="center"
                >
                  <HelpOutlineIcon sx={{ mr: 0.5 }} /> {t("form:helper.missingConfiguration")}
                </Typography>
              )}
            </>
          )}
        </Stack>
        <Stack direction="row" justifyContent="flex-end" alignItems="center" flex={0.1} spacing={0.5}>
          <Tooltip title={t("tooltip:title.duplicateThisConfiguration")}>
            <Button size="small" className="config-content-row-delete" onClick={() => duplicateConfig(item.id)}>
              <ContentCopyIcon color="action" />
            </Button>
          </Tooltip>
          {(item.status == BasketItemStatus.OptionsDone || item.selectedProduct) && (
            <Tooltip title={t("tooltip:title.applyConfigurationToAll")}>
              <Button size="small" className="config-content-row-delete" onClick={() => applyConfigToAll(item.id)}>
                <CopyAllIcon color="action" />
              </Button>
            </Tooltip>
          )}
          <Button size="small" className="config-content-row-delete" onClick={() => onRemove(item.id)}>
            <DeleteOutlinedIcon color="error" />
          </Button>
        </Stack>
      </Row>
    </label>
  );
};

const Row = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "checked",
})<{ checked: boolean }>(({ theme, checked }) => ({
  borderRadius: 6,
  overflow: "hidden",
  border: `1px solid ${theme.palette.backgroundAlpha.border}`,
  background: theme.palette.backgroundAlpha.main,
  padding: theme.spacing(0.5),
  cursor: "pointer",
  transition: "background .3s",
  ".config-content-row-delete": {
    opacity: 0,
    transition: "opacity .3s",
  },
  "&:hover": {
    ...(!checked && {
      background: theme.palette.backgroundAlpha.hover,
    }),
    ".MuiButtonBase-root": {
      opacity: 1,
    },
  },
  ...(checked && {
    background: theme.palette.primary.alpha,
    borderColor: theme.palette.primary.main,
  }),
}));

export default ConfigOrderContentsRow;
